import React, { useState, useEffect, useRef } from 'react';
import { User, Mail, Phone, Building, Shield, Edit2, Save, X, Camera, Upload, Lock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { userAPI } from '../services/api';
import PageHeader from '../components/layout/PageHeader';
import Card, { CardHeader, CardTitle, CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Badge from '../components/ui/Badge';

const Profile = () => {
  const { user, logout, refreshUser } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });

  // Change-password state (kept separate from the profile form/message)
  const [pwData, setPwData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [pwLoading, setPwLoading] = useState(false);
  const [pwMessage, setPwMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || ''
      });
    }
  }, [user]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await userAPI.updateUser(user._id, formData);

      if (response.data.success) {
        setMessage({ type: 'success', text: 'Profile updated successfully!' });
        setIsEditing(false);

        // Refresh the page to update user data in context
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to update profile'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      name: user.name || '',
      email: user.email || '',
      phone: user.phone || ''
    });
    setIsEditing(false);
    setMessage({ type: '', text: '' });
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setMessage({ type: 'error', text: 'Please select an image file' });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'Image size should be less than 5MB' });
      return;
    }

    setPhotoUploading(true);
    setMessage({ type: '', text: '' });

    try {
      const formData = new FormData();
      formData.append('photo', file);

      const response = await userAPI.uploadPhoto(user._id, formData);

      if (response.data.success) {
        setMessage({ type: 'success', text: 'Photo uploaded successfully!' });
        // Refresh user data
        if (refreshUser) {
          await refreshUser();
        } else {
          setTimeout(() => window.location.reload(), 1500);
        }
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to upload photo'
      });
    } finally {
      setPhotoUploading(false);
    }
  };

  const handlePwChange = (e) => {
    setPwData({ ...pwData, [e.target.name]: e.target.value });
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPwMessage({ type: '', text: '' });

    if (pwData.newPassword.length < 6) {
      setPwMessage({ type: 'error', text: 'New password must be at least 6 characters' });
      return;
    }
    if (pwData.newPassword !== pwData.confirmPassword) {
      setPwMessage({ type: 'error', text: 'New password and confirmation do not match' });
      return;
    }

    setPwLoading(true);
    try {
      // If the user entered a current password, verify+change it. If they left it
      // blank (OTP/first-time accounts that have no password yet), just set one.
      const hasCurrent = (pwData.currentPassword || '').trim().length > 0;
      const response = hasCurrent
        ? await userAPI.changePassword({
            currentPassword: pwData.currentPassword,
            newPassword: pwData.newPassword
          })
        : await userAPI.setPassword({ newPassword: pwData.newPassword });

      if (response.data.success) {
        setPwMessage({ type: 'success', text: hasCurrent ? 'Password changed successfully!' : 'Password set successfully!' });
        setPwData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      }
    } catch (error) {
      setPwMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to change password'
      });
    } finally {
      setPwLoading(false);
    }
  };

  const getRoleBadge = (role) => {
    const roleConfig = {
      admin: { variant: 'danger', label: 'Admin' },
      owner: { variant: 'info', label: 'Owner' },
      staff: { variant: 'warning', label: 'Staff' },
      driver: { variant: 'success', label: 'Driver' },
      customer: { variant: 'secondary', label: 'Customer' }
    };

    const config = roleConfig[role] || roleConfig.customer;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <p className="text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="My Profile"
        description="Manage your account settings and personal information"
      />

      {/* Message Alert */}
      {message.text && (
        <div
          className={`p-4 rounded-lg ${
            message.type === 'success'
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}
        >
          {message.text}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Overview Card */}
        <Card className="lg:col-span-1">
          <CardContent className="p-6">
            <div className="flex flex-col items-center">
              {/* Profile Photo with Upload */}
              <div className="relative group">
                <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-white shadow-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                  {user.photo?.url ? (
                    <img
                      src={user.photo.url}
                      alt={user.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-white text-4xl font-bold">
                      {user.name?.charAt(0)?.toUpperCase() || 'U'}
                    </span>
                  )}
                </div>
                {/* Upload Overlay */}
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={photoUploading}
                  className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                >
                  {photoUploading ? (
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                  ) : (
                    <Camera className="h-8 w-8 text-white" />
                  )}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  className="hidden"
                />
                {/* Camera Icon Badge */}
                <div className="absolute bottom-0 right-0 bg-blue-600 rounded-full p-2 border-2 border-white shadow-lg cursor-pointer hover:bg-blue-700 transition-colors"
                     onClick={() => fileInputRef.current?.click()}>
                  <Camera className="h-4 w-4 text-white" />
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mt-4">{user.name}</h2>
              <p className="text-gray-600 mt-1">{user.email}</p>
              <div className="mt-3">{getRoleBadge(user.role)}</div>

              <div className="w-full mt-6 space-y-3">
                <div className="flex items-center text-sm">
                  <Mail className="h-4 w-4 text-gray-400 mr-3" />
                  <span className="text-gray-600">{user.email}</span>
                </div>
                <div className="flex items-center text-sm">
                  <Phone className="h-4 w-4 text-gray-400 mr-3" />
                  <span className="text-gray-600">{user.phone || 'Not provided'}</span>
                </div>
                <div className="flex items-center text-sm">
                  <Shield className="h-4 w-4 text-gray-400 mr-3" />
                  <span className="text-gray-600 capitalize">{user.role}</span>
                </div>
                {user.organizationId && (
                  <div className="flex items-center text-sm">
                    <Building className="h-4 w-4 text-gray-400 mr-3" />
                    <span className="text-gray-600">Organization Member</span>
                  </div>
                )}
              </div>

              <Button
                variant="destructive"
                className="w-full mt-6"
                onClick={logout}
              >
                Logout
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Profile Edit Form */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Personal Information</CardTitle>
              {!isEditing && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsEditing(true)}
                >
                  <Edit2 className="h-4 w-4 mr-2" />
                  Edit Profile
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input
                  label="Full Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  disabled={!isEditing}
                  required
                />

                <Input
                  label="Email Address"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={!isEditing}
                  required
                />

                <Input
                  label="Phone Number"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  disabled={!isEditing}
                  placeholder="+1 (555) 000-0000"
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Role
                  </label>
                  <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                    <span className="text-gray-900 capitalize">{user.role}</span>
                  </div>
                </div>
              </div>

              {isEditing && (
                <div className="flex items-center gap-3 pt-4 border-t">
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={loading}
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {loading ? 'Saving...' : 'Save Changes'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCancel}
                    disabled={loading}
                  >
                    <X className="h-4 w-4 mr-2" />
                    Cancel
                  </Button>
                </div>
              )}
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Password — any logged-in user can set/change their own password here */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Lock className="h-5 w-5 mr-2 text-gray-500" />
            Set / Change Password
          </CardTitle>
        </CardHeader>
        <CardContent>
          {pwMessage.text && (
            <div
              className={`mb-4 p-3 rounded-lg text-sm ${
                pwMessage.type === 'success'
                  ? 'bg-green-50 text-green-800 border border-green-200'
                  : 'bg-red-50 text-red-800 border border-red-200'
              }`}
            >
              {pwMessage.text}
            </div>
          )}
          <p className="mb-4 text-sm text-gray-500">
            If you don't have a password yet (e.g. you log in with OTP), leave “Current Password” blank.
          </p>
          <form onSubmit={handlePasswordSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Current Password (leave blank if none)"
                name="currentPassword"
                type="password"
                value={pwData.currentPassword}
                onChange={handlePwChange}
                autoComplete="current-password"
              />
              {/* spacer keeps New/Confirm aligned on the second row */}
              <div className="hidden md:block" />
              <Input
                label="New Password"
                name="newPassword"
                type="password"
                value={pwData.newPassword}
                onChange={handlePwChange}
                autoComplete="new-password"
                placeholder="At least 6 characters"
                required
              />
              <Input
                label="Confirm New Password"
                name="confirmPassword"
                type="password"
                value={pwData.confirmPassword}
                onChange={handlePwChange}
                autoComplete="new-password"
                required
              />
            </div>
            <div className="flex items-center justify-between pt-4 border-t">
              <span className="text-sm text-gray-500">
                Forgot your current password? Use “Forgot password” on the login screen.
              </span>
              <Button type="submit" variant="primary" disabled={pwLoading}>
                <Lock className="h-4 w-4 mr-2" />
                {pwLoading ? 'Saving...' : 'Save Password'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Additional Information */}
      <Card>
        <CardHeader>
          <CardTitle>Account Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                User ID
              </label>
              <p className="text-gray-900 font-mono text-sm">{user._id}</p>
            </div>
            {user.organizationId && (
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Organization ID
                </label>
                <p className="text-gray-900 font-mono text-sm">{user.organizationId}</p>
              </div>
            )}
            {user.ownerId && (
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Owner ID
                </label>
                <p className="text-gray-900 font-mono text-sm">{user.ownerId}</p>
              </div>
            )}
            {user.permissions && user.permissions.length > 0 && (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-600 mb-2">
                  Permissions
                </label>
                <div className="flex flex-wrap gap-2">
                  {user.permissions.map((permission, index) => (
                    <Badge key={index} variant="secondary">
                      {permission}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Profile;
